import { currentLocale } from '@shared/i18n';
/**
 * Formatting helpers. Uses browser Intl.* — no library dependencies.
 */

export function formatMoney(amount: number | null | undefined, currency = 'KGS'): string {
  if (amount == null || Number.isNaN(amount)) return '—';
  try {
    return new Intl.NumberFormat('ru-KG', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount)} ${currency}`;
  }
}

// Parse a user-typed price. Accepts plain digits, comma/space thousands separators, a decimal
// point, and a K (thousand) / M (million) suffix: "130,000" | "130k" | "1.5m" → number.
export function parsePrice(s: string | null | undefined): number | null {
  if (s == null) return null;
  let t = String(s).trim().toLowerCase().replace(/[\s,]/g, '');
  if (!t) return null;
  let mult = 1;
  if (t.endsWith('k')) { mult = 1_000; t = t.slice(0, -1); }
  else if (t.endsWith('m')) { mult = 1_000_000; t = t.slice(0, -1); }
  const n = parseFloat(t);
  if (!isFinite(n)) return null;
  return Math.round(n * mult);
}

// Accounting-style display: 130000 → "130,000" (comma thousands, no decimals).
export function formatPrice(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return '';
  // Prices are KG som — use ru-RU grouping (space) so "120 000" matches the app,
  // not the en-US comma ("120,000").
  return Math.round(n).toLocaleString('ru-RU');
}

export function formatDate(d: string | Date | null | undefined, locale?: string): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale ?? currentLocale(), {
    year: 'numeric', month: 'short', day: 'numeric',
  }).format(date);
}

export function formatDateTime(d: string | Date | null | undefined, locale?: string): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale ?? currentLocale(), {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date);
}

export function relativeTime(d: string | Date | null | undefined, locale?: string): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  const diffSec = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(locale ?? currentLocale(), { numeric: 'auto' });
  if (abs < 60) return rtf.format(diffSec, 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  return rtf.format(Math.round(diffSec / 86400), 'day');
}
